import React, { useEffect, useMemo, useState } from "react";
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Badge,
    Box,
    Button,
    Divider,
    FormControl,
    FormLabel,
    HStack,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    SimpleGrid,
    Spinner,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Text,
    Tooltip,
    VStack,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { RefreshCw, ShieldCheck, WalletCards } from "lucide-react";
import { PayoutType, financeApi } from "./financeApi";
import FinancePageShell from "./common/FinancePageShell";
import FinanceSectionCard from "./common/FinanceSectionCard";
import FinanceStatCard from "./common/FinanceStatCard";
import FinanceDataTable, {
    FinanceDataTableColumn,
} from "./common/FinanceDataTable";
import { formatDateTime, formatMoney } from "./common/formatters";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

type PeriodType = "weekly" | "monthly";

type BatchRow = {
    _id: string;
    payoutType?: PayoutType;
    periodType?: PeriodType;
    status?: string;
    totalAmount?: number;
    beneficiaryCount?: number;
    sellerCount?: number;
    createdBy?: string;
    createdAt?: string;
};

type PayoutGroup = {
    key: string;
    payoutType: PayoutType;
    ownerId: string;
    ownerName?: string;
    periodType: PeriodType;
    amount: number;
    currency?: string;
    batchCount: number;
    itemCount: number;
    batches: BatchRow[];
};

const isOpenStatus = (status?: string) =>
    status !== "paid" && status !== "failed" && status !== "cancelled";

const getStatusScheme = (status?: string) => {
    if (status === "paid") return "green";
    if (status === "processing") return "blue";
    if (status === "failed") return "red";
    return "gray";
};

const getPayoutTypeLabel = (type?: string) =>
    type === "driver" ? "Driver" : "Seller";

const normalizeGroup = (row: any): PayoutGroup | null => {
    const payoutType = (row.payoutType || "seller") as PayoutType;
    const ownerId = String(
        row.ownerId || row.sellerUserId || row.driverId || "",
    ).trim();
    const periodType = (row.periodType || "weekly") as PeriodType;

    if (!ownerId) return null;

    return {
        key: row.key || `${payoutType}_${ownerId}_${periodType}`,
        payoutType,
        ownerId,
        ownerName: row.ownerName || row.displayName || row.name || "",
        periodType,
        amount: Number(row.amount || row.totalAmount || row.outstandingAmount || 0),
        currency: row.currency || "MWK",
        batchCount: Number(row.batchCount || row.batches?.length || 0),
        itemCount: Number(row.itemCount || 0),
        batches: row.batches || [],
    };
};

const AdminPayoutBatchesPage: React.FC = () => {
    const toast = useToast();
    const { theme } = useCustomTheme();

    const payAllModal = useDisclosure();
    const payBatchModal = useDisclosure();

    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [payingAll, setPayingAll] = useState(false);
    const [payingBatch, setPayingBatch] = useState(false);

    const [payoutType, setPayoutType] = useState<PayoutType>("seller");
    const [periodType, setPeriodType] = useState<PeriodType>("weekly");

    const [rows, setRows] = useState<BatchRow[]>([]);
    const [groups, setGroups] = useState<PayoutGroup[]>([]);

    const [selectedGroup, setSelectedGroup] = useState<PayoutGroup | null>(null);
    const [selectedBatch, setSelectedBatch] = useState<BatchRow | null>(null);

    const [payAllReference, setPayAllReference] = useState("");
    const [payBatchReference, setPayBatchReference] = useState("");

    const load = async () => {
        try {
            setLoading(true);

            const res = await financeApi.getPayoutBatches();

            setRows(res?.data || []);

            const nextGroups = (res?.groups || res?.consolidated || [])
                .map(normalizeGroup)
                .filter(Boolean) as PayoutGroup[];

            setGroups(nextGroups);
        } catch (error: any) {
            toast({
                status: "error",
                title: "Failed to load payout batches",
                description:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Please try again.",
            });
        } finally {
            setLoading(false);
        }
    };

    const createBatch = async () => {
        try {
            setCreating(true);

            const res = await financeApi.createPayoutBatch({
                payoutType,
                periodType,
                createdBy: "admin",
            });

            const created = res?.data;

            toast({
                status: "success",
                title: `${getPayoutTypeLabel(payoutType)} payout updated`,
                description: created?.totalAmount
                    ? `Outstanding: ${formatMoney(created.totalAmount)}`
                    : "No eligible unpaid records were found.",
            });

            await load();
        } catch (error: any) {
            toast({
                status: "error",
                title: "Failed to create or update payout batch",
                description:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Please try again.",
            });
        } finally {
            setCreating(false);
        }
    };

    const openPayAll = (group: PayoutGroup) => {
        setSelectedGroup(group);
        setPayAllReference("");
        payAllModal.onOpen();
    };

    const closePayAll = () => {
        if (payingAll) return;
        setSelectedGroup(null);
        setPayAllReference("");
        payAllModal.onClose();
    };

    const openPayBatch = (batch: BatchRow) => {
        setSelectedBatch(batch);
        setPayBatchReference("");
        payBatchModal.onOpen();
    };

    const closePayBatch = () => {
        if (payingBatch) return;
        setSelectedBatch(null);
        setPayBatchReference("");
        payBatchModal.onClose();
    };

    const payAllForGroup = async () => {
        if (!selectedGroup) return;

        const reference = payAllReference.trim();

        if (!reference) {
            toast({
                status: "warning",
                title: "Transfer reference required",
                description: "Add the bank, Airtel Money, Mpamba, or provider reference.",
            });
            return;
        }

        try {
            setPayingAll(true);

            await financeApi.payConsolidatedPayout({
                payoutType: selectedGroup.payoutType,
                ownerId: selectedGroup.ownerId,
                periodType: selectedGroup.periodType,
                transferReference: reference,
            });

            toast({
                status: "success",
                title: "Payout marked paid",
                description: `${getPayoutTypeLabel(
                    selectedGroup.payoutType,
                )} payout of ${formatMoney(
                    selectedGroup.amount,
                    selectedGroup.currency,
                )} was marked paid across ${selectedGroup.batchCount} batch(es).`,
            });

            closePayAll();
            await load();
        } catch (error: any) {
            toast({
                status: "error",
                title: "Failed to pay accumulated payout",
                description:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Please try again.",
            });
        } finally {
            setPayingAll(false);
        }
    };

    const paySingleBatch = async () => {
        if (!selectedBatch?._id) return;

        const reference = payBatchReference.trim();

        if (!reference) {
            toast({
                status: "warning",
                title: "Transfer reference required",
                description: "Add the bank, Airtel Money, Mpamba, or provider reference.",
            });
            return;
        }

        try {
            setPayingBatch(true);

            await financeApi.markPayoutBatchPaid(selectedBatch._id, {
                transferReference: reference,
            });

            toast({
                status: "success",
                title: "Batch marked paid",
                description: `Batch ${selectedBatch._id} was marked as paid.`,
            });

            closePayBatch();
            await load();
        } catch (error: any) {
            toast({
                status: "error",
                title: "Failed to pay this batch",
                description:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Please try again.",
            });
        } finally {
            setPayingBatch(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const summary = useMemo(() => {
        return groups.reduce(
            (acc, group) => {
                acc.groupCount += 1;
                acc.total += Number(group.amount || 0);

                if (group.payoutType === "seller") {
                    acc.sellerTotal += Number(group.amount || 0);
                }

                if (group.payoutType === "driver") {
                    acc.driverTotal += Number(group.amount || 0);
                }

                acc.batchCount += group.batchCount || group.batches.length;

                return acc;
            },
            {
                groupCount: 0,
                batchCount: 0,
                total: 0,
                sellerTotal: 0,
                driverTotal: 0,
            },
        );
    }, [groups]);

    const historySummary = useMemo(() => {
        return rows.reduce(
            (acc, row) => {
                const amount = Number(row.totalAmount || 0);

                acc.count += 1;
                acc.total += amount;

                if (row.payoutType === "seller") acc.sellerTotal += amount;
                if (row.payoutType === "driver") acc.driverTotal += amount;
                if (row.status === "paid") acc.paidTotal += amount;
                if (row.status !== "paid") acc.openTotal += amount;

                return acc;
            },
            {
                count: 0,
                total: 0,
                sellerTotal: 0,
                driverTotal: 0,
                paidTotal: 0,
                openTotal: 0,
            },
        );
    }, [rows]);

    const batchColumns: Array<FinanceDataTableColumn<BatchRow>> = [
        {
            key: "type",
            header: "Type",
            render: (row) => (
                <Text fontWeight="700" color={theme.text}>
                    {row.payoutType || "seller"}
                </Text>
            ),
        },
        {
            key: "period",
            header: "Period",
            render: (row) => row.periodType || "weekly",
        },
        {
            key: "status",
            header: "Status",
            render: (row) => (
                <Badge colorScheme={getStatusScheme(row.status)}>
                    {row.status || "draft"}
                </Badge>
            ),
        },
        {
            key: "total",
            header: "Total",
            isNumeric: true,
            render: (row) => formatMoney(row.totalAmount),
        },
        {
            key: "beneficiaries",
            header: "Beneficiaries",
            isNumeric: true,
            render: (row) => row.beneficiaryCount || row.sellerCount || 0,
        },
        {
            key: "createdBy",
            header: "Created By",
            render: (row) => row.createdBy || "system",
        },
        {
            key: "created",
            header: "Created",
            render: (row) => formatDateTime(row.createdAt),
        },
        {
            key: "actions",
            header: "",
            render: (row) => (
                <Button
                    as={RouterLink}
                    to={`/finance/admin/batches/${row._id}`}
                    size="sm"
                    variant="outline"
                >
                    Details
                </Button>
            ),
        },
    ];

    return (
        <FinancePageShell
            title="Payout Batches"
            subtitle="Same seller or driver payouts are grouped into one expandable section. Use the history tab when you need to audit every individual batch."
        >
            <SimpleGrid columns={{ base: 1, md: 2, xl: 5 }} spacing={4}>
                <FinanceStatCard label="Payee Groups" value={String(summary.groupCount)} />
                <FinanceStatCard label="Open Batches" value={String(summary.batchCount)} />
                <FinanceStatCard label="Ready To Pay" value={formatMoney(summary.total)} />
                <FinanceStatCard label="Seller Total" value={formatMoney(summary.sellerTotal)} />
                <FinanceStatCard label="Driver Total" value={formatMoney(summary.driverTotal)} />
            </SimpleGrid>

            <FinanceSectionCard
                title="Create / update payout batches"
                rightSlot={
                    <Button
                        leftIcon={<RefreshCw size={16} />}
                        size="sm"
                        onClick={load}
                        variant="outline"
                    >
                        Refresh
                    </Button>
                }
            >
                <Box
                    p={4}
                    borderRadius="16px"
                    border={`1px solid ${theme.border}`}
                    bg={theme.surfaceAlt}
                >
                    <HStack spacing={3} align="end" flexWrap="wrap">
                        <Box minW={{ base: "100%", md: "180px" }}>
                            <Text
                                mb={1}
                                color={theme.textMuted}
                                fontSize="sm"
                                fontWeight="700"
                            >
                                Payout Type
                            </Text>
                            <Select
                                value={payoutType}
                                onChange={(e) => setPayoutType(e.target.value as PayoutType)}
                                bg={theme.inputBg}
                                borderColor={theme.inputBorder}
                            >
                                <option value="seller">Seller payouts</option>
                                <option value="driver">Driver payouts</option>
                            </Select>
                        </Box>

                        <Box minW={{ base: "100%", md: "180px" }}>
                            <Text
                                mb={1}
                                color={theme.textMuted}
                                fontSize="sm"
                                fontWeight="700"
                            >
                                Period
                            </Text>
                            <Select
                                value={periodType}
                                onChange={(e) => setPeriodType(e.target.value as PeriodType)}
                                bg={theme.inputBg}
                                borderColor={theme.inputBorder}
                            >
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                            </Select>
                        </Box>

                        <Tooltip
                            hasArrow
                            label="Pulls currently eligible unpaid records into payout batches. It does not mark anyone paid."
                        >
                            <Button
                                leftIcon={<WalletCards size={16} />}
                                onClick={createBatch}
                                isLoading={creating}
                                color={theme.textOnPrimary}
                                bgImage={theme.gradients.primaryButton}
                                _hover={{ bgImage: theme.gradients.primaryButtonHover }}
                            >
                                Create / Update
                            </Button>
                        </Tooltip>
                    </HStack>
                </Box>
            </FinanceSectionCard>

            <Tabs variant="enclosed" isLazy lazyBehavior="keepMounted">
                <TabList>
                    <Tab fontWeight="800">Grouped payouts</Tab>
                    <Tab fontWeight="800">All batch history</Tab>
                </TabList>

                <TabPanels>
                    <TabPanel px={0}>
                        <FinanceSectionCard title="Grouped payouts">
                            <Box
                                mb={4}
                                p={4}
                                borderRadius="16px"
                                border={`1px solid ${theme.border}`}
                                bg={theme.surfaceAlt}
                            >
                                <HStack align="start" spacing={3}>
                                    <ShieldCheck size={18} color={theme.primary} />
                                    <Box>
                                        <Text color={theme.text} fontWeight="800">
                                            Pay by seller or driver
                                        </Text>
                                        <Text color={theme.textMuted} fontSize="sm">
                                            Each section below represents one seller or one driver. The
                                            top shows the total amount outstanding. Open the section to
                                            see every batch inside it and optionally pay one batch only.
                                        </Text>
                                    </Box>
                                </HStack>
                            </Box>

                            {loading ? (
                                <VStack py={10}>
                                    <Spinner />
                                    <Text color={theme.textMuted}>Loading grouped payouts...</Text>
                                </VStack>
                            ) : groups.length ? (
                                <Accordion allowMultiple defaultIndex={[0]}>
                                    {groups.map((group) => (
                                        <AccordionItem
                                            key={group.key}
                                            border={`1px solid ${theme.border}`}
                                            borderRadius="18px"
                                            mb={4}
                                            overflow="hidden"
                                            bg={theme.surface}
                                        >
                                            <AccordionButton px={4} py={4} _hover={{ bg: theme.surfaceAlt }}>
                                                <Box flex="1" textAlign="left">
                                                    <HStack spacing={3} flexWrap="wrap">
                                                        <Badge
                                                            colorScheme={
                                                                group.payoutType === "driver" ? "purple" : "teal"
                                                            }
                                                            borderRadius="full"
                                                            px={3}
                                                            py={1}
                                                        >
                                                            {getPayoutTypeLabel(group.payoutType)}
                                                        </Badge>

                                                        <Box>
                                                            <Text color={theme.text} fontWeight="900">
                                                                {group.ownerName || group.ownerId}
                                                            </Text>
                                                            {!!group.ownerName && (
                                                                <Text color={theme.textMuted} fontSize="xs">
                                                                    {group.ownerId}
                                                                </Text>
                                                            )}
                                                        </Box>
                                                    </HStack>
                                                </Box>

                                                <HStack spacing={6} display={{ base: "none", md: "flex" }}>
                                                    <Box textAlign="right">
                                                        <Text
                                                            color={theme.textMuted}
                                                            fontSize="xs"
                                                            fontWeight="700"
                                                        >
                                                            Total To Pay
                                                        </Text>
                                                        <Text color={theme.text} fontWeight="900">
                                                            {formatMoney(group.amount, group.currency)}
                                                        </Text>
                                                    </Box>

                                                    <Box textAlign="right">
                                                        <Text
                                                            color={theme.textMuted}
                                                            fontSize="xs"
                                                            fontWeight="700"
                                                        >
                                                            Batches
                                                        </Text>
                                                        <Text color={theme.text} fontWeight="800">
                                                            {group.batchCount}
                                                        </Text>
                                                    </Box>
                                                </HStack>

                                                <AccordionIcon ml={4} />
                                            </AccordionButton>

                                            <AccordionPanel px={4} pb={4}>
                                                <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3} mb={4}>
                                                    <Box
                                                        p={3}
                                                        borderRadius="14px"
                                                        bg={theme.surfaceAlt}
                                                        border={`1px solid ${theme.border}`}
                                                    >
                                                        <Text
                                                            color={theme.textMuted}
                                                            fontSize="xs"
                                                            fontWeight="700"
                                                        >
                                                            Total To Pay
                                                        </Text>
                                                        <Text color={theme.text} fontWeight="900">
                                                            {formatMoney(group.amount, group.currency)}
                                                        </Text>
                                                    </Box>

                                                    <Box
                                                        p={3}
                                                        borderRadius="14px"
                                                        bg={theme.surfaceAlt}
                                                        border={`1px solid ${theme.border}`}
                                                    >
                                                        <Text
                                                            color={theme.textMuted}
                                                            fontSize="xs"
                                                            fontWeight="700"
                                                        >
                                                            Open Batches
                                                        </Text>
                                                        <Text color={theme.text} fontWeight="900">
                                                            {group.batchCount}
                                                        </Text>
                                                    </Box>

                                                    <Box
                                                        p={3}
                                                        borderRadius="14px"
                                                        bg={theme.surfaceAlt}
                                                        border={`1px solid ${theme.border}`}
                                                    >
                                                        <Text
                                                            color={theme.textMuted}
                                                            fontSize="xs"
                                                            fontWeight="700"
                                                        >
                                                            Period
                                                        </Text>
                                                        <Text color={theme.text} fontWeight="900">
                                                            {group.periodType}
                                                        </Text>
                                                    </Box>

                                                    <Button
                                                        leftIcon={<WalletCards size={16} />}
                                                        onClick={() => openPayAll(group)}
                                                        h="100%"
                                                        minH="64px"
                                                        color={theme.textOnPrimary}
                                                        bgImage={theme.gradients.primaryButton}
                                                        _hover={{
                                                            bgImage: theme.gradients.primaryButtonHover,
                                                        }}
                                                    >
                                                        Pay All
                                                    </Button>
                                                </SimpleGrid>

                                                <Divider borderColor={theme.border} mb={4} />

                                                <FinanceDataTable
                                                    rows={group.batches}
                                                    columns={[
                                                        {
                                                            key: "batch",
                                                            header: "Batch",
                                                            render: (batch) => (
                                                                <>
                                                                    <Text color={theme.text} fontWeight="700">
                                                                        {batch._id}
                                                                    </Text>
                                                                    <Text color={theme.textMuted} fontSize="xs">
                                                                        {batch.payoutType || group.payoutType} /{" "}
                                                                        {batch.periodType || group.periodType}
                                                                    </Text>
                                                                </>
                                                            ),
                                                        },
                                                        {
                                                            key: "status",
                                                            header: "Status",
                                                            render: (batch) => (
                                                                <Badge colorScheme={getStatusScheme(batch.status)}>
                                                                    {batch.status || "draft"}
                                                                </Badge>
                                                            ),
                                                        },
                                                        {
                                                            key: "amount",
                                                            header: "Amount",
                                                            isNumeric: true,
                                                            render: (batch) => formatMoney(batch.totalAmount),
                                                        },
                                                        {
                                                            key: "beneficiaries",
                                                            header: "Beneficiaries",
                                                            isNumeric: true,
                                                            render: (batch) =>
                                                                batch.beneficiaryCount || batch.sellerCount || 1,
                                                        },
                                                        {
                                                            key: "createdBy",
                                                            header: "Created By",
                                                            render: (batch) => batch.createdBy || "system",
                                                        },
                                                        {
                                                            key: "created",
                                                            header: "Created",
                                                            render: (batch) => formatDateTime(batch.createdAt),
                                                        },
                                                        {
                                                            key: "actions",
                                                            header: "",
                                                            render: (batch) => (
                                                                <HStack justify="flex-end">
                                                                    {isOpenStatus(batch.status) &&
                                                                        Number(batch.totalAmount || 0) > 0 && (
                                                                            <Button
                                                                                size="xs"
                                                                                leftIcon={<WalletCards size={13} />}
                                                                                onClick={() => openPayBatch(batch)}
                                                                            >
                                                                                Pay This
                                                                            </Button>
                                                                        )}

                                                                    <Button
                                                                        as={RouterLink}
                                                                        to={`/finance/admin/batches/${batch._id}`}
                                                                        size="xs"
                                                                        variant="outline"
                                                                    >
                                                                        Details
                                                                    </Button>
                                                                </HStack>
                                                            ),
                                                        },
                                                    ]}
                                                    getRowKey={(batch, index) => String(batch._id || index)}
                                                    emptyText="No batches found for this payee."
                                                    initialPageSize={5}
                                                />
                                            </AccordionPanel>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            ) : (
                                <Box
                                    p={5}
                                    borderRadius="16px"
                                    border={`1px dashed ${theme.border}`}
                                    bg={theme.surfaceAlt}
                                >
                                    <Text color={theme.text} fontWeight="800">
                                        No grouped payouts ready
                                    </Text>
                                    <Text color={theme.textMuted} mt={1} fontSize="sm">
                                        Create or update a payout batch when sellers or drivers have
                                        eligible unpaid records.
                                    </Text>
                                </Box>
                            )}
                        </FinanceSectionCard>
                    </TabPanel>

                    <TabPanel px={0}>
                        <FinanceSectionCard title="All batch history">
                            <SimpleGrid columns={{ base: 1, md: 2, xl: 5 }} spacing={4} mb={4}>
                                <FinanceStatCard
                                    label="Batches"
                                    value={String(historySummary.count)}
                                />
                                <FinanceStatCard
                                    label="Total Listed"
                                    value={formatMoney(historySummary.total)}
                                />
                                <FinanceStatCard
                                    label="Seller Total"
                                    value={formatMoney(historySummary.sellerTotal)}
                                />
                                <FinanceStatCard
                                    label="Driver Total"
                                    value={formatMoney(historySummary.driverTotal)}
                                />
                                <FinanceStatCard
                                    label="Still Open"
                                    value={formatMoney(historySummary.openTotal)}
                                />
                            </SimpleGrid>

                            {loading ? (
                                <VStack py={10}>
                                    <Spinner />
                                    <Text color={theme.textMuted}>Loading payout batches...</Text>
                                </VStack>
                            ) : (
                                <FinanceDataTable
                                    rows={rows}
                                    columns={batchColumns}
                                    getRowKey={(row, index) => String(row._id || index)}
                                    emptyText="No payout batches found yet."
                                    initialPageSize={10}
                                />
                            )}
                        </FinanceSectionCard>
                    </TabPanel>
                </TabPanels>
            </Tabs>

            <Modal isOpen={payAllModal.isOpen} onClose={closePayAll} isCentered size="lg">
                <ModalOverlay />
                <ModalContent bg={theme.surface} borderRadius="20px">
                    <ModalHeader color={theme.text}>Pay all grouped batches</ModalHeader>
                    <ModalCloseButton isDisabled={payingAll} />

                    <ModalBody>
                        <VStack align="stretch" spacing={4}>
                            <Box
                                p={4}
                                borderRadius="16px"
                                bg={theme.surfaceAlt}
                                border={`1px solid ${theme.border}`}
                            >
                                <HStack justify="space-between" align="start">
                                    <Box>
                                        <Text color={theme.textMuted} fontSize="xs" fontWeight="700">
                                            Payee
                                        </Text>
                                        <Text color={theme.text} fontWeight="900">
                                            {selectedGroup?.ownerName || selectedGroup?.ownerId || "—"}
                                        </Text>
                                        {!!selectedGroup?.ownerName && (
                                            <Text color={theme.textMuted} fontSize="xs">
                                                {selectedGroup.ownerId}
                                            </Text>
                                        )}
                                    </Box>

                                    <Badge
                                        colorScheme={
                                            selectedGroup?.payoutType === "driver" ? "purple" : "teal"
                                        }
                                    >
                                        {getPayoutTypeLabel(selectedGroup?.payoutType)}
                                    </Badge>
                                </HStack>

                                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mt={4}>
                                    <Box>
                                        <Text color={theme.textMuted} fontSize="xs" fontWeight="700">
                                            Total Amount
                                        </Text>
                                        <Text color={theme.text} fontWeight="900">
                                            {formatMoney(selectedGroup?.amount, selectedGroup?.currency)}
                                        </Text>
                                    </Box>

                                    <Box>
                                        <Text color={theme.textMuted} fontSize="xs" fontWeight="700">
                                            Batches
                                        </Text>
                                        <Text color={theme.text} fontWeight="900">
                                            {selectedGroup?.batchCount || 0}
                                        </Text>
                                    </Box>

                                    <Box>
                                        <Text color={theme.textMuted} fontSize="xs" fontWeight="700">
                                            Period
                                        </Text>
                                        <Text color={theme.text} fontWeight="900">
                                            {selectedGroup?.periodType || "weekly"}
                                        </Text>
                                    </Box>
                                </SimpleGrid>
                            </Box>

                            <FormControl isRequired>
                                <FormLabel color={theme.text}>Transfer reference</FormLabel>
                                <Input
                                    value={payAllReference}
                                    onChange={(e) => setPayAllReference(e.target.value)}
                                    placeholder="Bank / Airtel Money / Mpamba reference"
                                    bg={theme.inputBg}
                                    borderColor={theme.inputBorder}
                                />
                            </FormControl>

                            <Text color={theme.textMuted} fontSize="sm">
                                This marks all open batches for this same seller or driver as paid.
                                You can still pay one batch individually from inside the section.
                            </Text>
                        </VStack>
                    </ModalBody>

                    <ModalFooter gap={2}>
                        <Button variant="outline" onClick={closePayAll} isDisabled={payingAll}>
                            Cancel
                        </Button>
                        <Button
                            onClick={payAllForGroup}
                            isLoading={payingAll}
                            color={theme.textOnPrimary}
                            bgImage={theme.gradients.primaryButton}
                            _hover={{ bgImage: theme.gradients.primaryButtonHover }}
                        >
                            Confirm Pay All
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={payBatchModal.isOpen} onClose={closePayBatch} isCentered>
                <ModalOverlay />
                <ModalContent bg={theme.surface} borderRadius="20px">
                    <ModalHeader color={theme.text}>Pay this batch only</ModalHeader>
                    <ModalCloseButton isDisabled={payingBatch} />

                    <ModalBody>
                        <VStack align="stretch" spacing={4}>
                            <Box
                                p={4}
                                borderRadius="16px"
                                bg={theme.surfaceAlt}
                                border={`1px solid ${theme.border}`}
                            >
                                <Text color={theme.textMuted} fontSize="xs" fontWeight="700">
                                    Batch
                                </Text>
                                <Text color={theme.text} fontWeight="700" wordBreak="break-all">
                                    {selectedBatch?._id || "—"}
                                </Text>

                                <Text color={theme.textMuted} fontSize="xs" fontWeight="700" mt={3}>
                                    Amount
                                </Text>
                                <Text color={theme.text} fontWeight="900">
                                    {formatMoney(selectedBatch?.totalAmount)}
                                </Text>
                            </Box>

                            <FormControl isRequired>
                                <FormLabel color={theme.text}>Transfer reference</FormLabel>
                                <Input
                                    value={payBatchReference}
                                    onChange={(e) => setPayBatchReference(e.target.value)}
                                    placeholder="Bank / Airtel Money / Mpamba reference"
                                    bg={theme.inputBg}
                                    borderColor={theme.inputBorder}
                                />
                            </FormControl>

                            <Text color={theme.textMuted} fontSize="sm">
                                This marks only this selected batch as paid. Other open batches for
                                the same seller or driver will remain unpaid.
                            </Text>
                        </VStack>
                    </ModalBody>

                    <ModalFooter gap={2}>
                        <Button variant="outline" onClick={closePayBatch} isDisabled={payingBatch}>
                            Cancel
                        </Button>
                        <Button
                            onClick={paySingleBatch}
                            isLoading={payingBatch}
                            color={theme.textOnPrimary}
                            bgImage={theme.gradients.primaryButton}
                            _hover={{ bgImage: theme.gradients.primaryButtonHover }}
                        >
                            Confirm This Batch Paid
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </FinancePageShell>
    );
};

export default AdminPayoutBatchesPage;